import { User, useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import { usersv2Client } from '@/adapters/schemaClients';
import { UserPayload } from '@/hooks/useUserQuery';
import { Intercom } from '@/lib/Intercom';
import { PostHog } from '@/lib/PostHog';
import { Pylon } from '@/lib/Pylon';
import { UserModel } from '@/models/UserModel';

export interface ExaCareAuth0User extends User {
  organization_id?: string;
  role?: string;
}

function toExaCareAuth0User(user: User = {}) {
  return Object.fromEntries(
    Object.entries(user).map(([key, value]) => {
      const last = key.lastIndexOf('/');
      if (last >= 0) {
        return [key.slice(last + 1), value];
      }
      return [key, value];
    })
  ) as ExaCareAuth0User;
}

export const USE_CURRENT_USER_QUERY_KEY = 'useCurrentUser';

export const useCurrentUser = (options: UseQueryOptions<UserModel> = {}) => {
  const { isAuthenticated, user: auth0User } = useAuth0();

  return useQuery<UserModel>(
    [USE_CURRENT_USER_QUERY_KEY],
    async () => {
      const { data: user } = await usersv2Client.getCurrentUser();
      if (!user) {
        throw new Error('Current user not found');
      }
      const backendEmail = user.email;
      const userModel = new UserModel(
        Object.assign(
          user,
          toExaCareAuth0User(auth0User)
        ) as unknown as UserPayload
      );

      if (import.meta.env.MODE === 'production') {
        window.Upscope('updateConnection', {
          uniqueId: user.id,

          identities: [user.first_name, user.last_name, user.email, user.phone]
        });
      }

      if (import.meta.env.PROD) {
        Sentry.setUser({
          email: user.email,
          id: user.id,
          username: user.username ?? undefined
        });
      }

      Intercom.setUser(userModel);
      Pylon.setChatSettings(userModel, backendEmail);
      Pylon.setNewIssueCustomFields(userModel);
      PostHog.identify(userModel);
      return userModel;
    },
    {
      enabled: isAuthenticated,
      refetchOnMount: false,
      ...options
    }
  );
};
